import analytics, { IdentifyTraits } from '.';
import { getFiGrowthBook } from '../../appview/lib/growthbook';

export function identifyUser(userId: string, traits: IdentifyTraits) {
  analytics.identify(userId, traits);

  getFiGrowthBook().setAttributes();
}

export function resetAnalytics() {
  analytics.reset();
}

export function trackNewsletterSignup(email: string, source: string) {
  analytics.track('email_captured', {
    name: email.toLowerCase(),
    source,
  });
}

export function trackExperimentViewed(name: string, variant: string) {
  analytics.track('User Viewed Experiment', {
    name,
    variant,
  });
}

/**
 * Impression event for the nano upsell offer block.
 *
 * Clickthrough on this step is measured today as taps / `/register/upgrade` pageviews. That
 * pageview disappears when the confirmation and offer screens merge into one, so this event
 * becomes the denominator. It ships ahead of the merge on purpose: running both instruments
 * side by side first means the before/after comparison is same-instrument, rather than
 * comparing pageview-denominated to in-view-denominated rates across the change itself.
 */
export function trackNanoUpsellOfferViewed(properties: { petId: string; gwa: boolean }) {
  analytics.track('Nano Registration > Upsell > Offer Viewed', properties);
}

interface NanoUpsellProperties {
  petId: string;
  gwa: boolean;
}

/**
 * Denominator for the merged confirmation + offer screen. It renders at `/register/success`, the
 * same path as the unmerged version, so the automatic pageview cannot tell the two variants apart.
 */
export function trackNanoUpsellPageViewed(properties: NanoUpsellProperties & { isTransfer: boolean }) {
  analytics.track('Nano Registration > Upsell > Page Viewed', properties);
}

/**
 * The two actions on the offer screen. These reuse the names UpgradeStepV2 fires so the funnel
 * survives the merge on one instrument, for the same reason as `trackNanoUpsellOfferViewed`.
 */
export function trackNanoUpsellShopTapped(properties: NanoUpsellProperties) {
  analytics.track('Nano Registration > Upsell > Tapped Try It Button', properties);
}

export function trackNanoUpsellSkipTapped(properties: NanoUpsellProperties) {
  analytics.track('Nano Registration > Upsell > Tapped Skip Deal Button', properties);
}

export function trackClickShopCollarCTA(component: string) {
  analytics.track('Nano Registration > Success > Get the Collar Button', {
    component,
  });
}

/**
 * Events for the merged confirmation + offer screen.
 *
 * These are deliberately new names rather than more `> Upsell >` events. The `> Upsell >` events
 * above reuse the names `UpgradeStepV2` fires, so the before/after comparison across the merge ran
 * on a single instrument — that comparison is now done (shop-CTA per completed registration went
 * 30.0% -> 56.6%). Keeping them as the everyday funnel has two problems:
 *
 *   1. The primary CTA fires BOTH `Upsell > Tapped Try It Button` and
 *      `Success > Get the Collar Button`. Any raw count of either is wrong.
 *   2. `component` is not one of the columns the warehouse exposes from a Segment payload, so
 *      merged-screen taps cannot be separated from legacy success-page taps there.
 *
 * The names below are one event per action on one screen, so the funnel is a group-by on the event
 * name with no de-duplication. The legacy events keep firing unchanged — Customer.io campaigns
 * trigger on them, and removing them would break those.
 */
export interface NanoSuccessOfferProperties {
  /** Lands in the warehouse `value` column, which is how these events get a queryable dimension. */
  petId: string;
  gwa: boolean;
  /** A pending transfer request and an active chip arrive at the same screen in different states. */
  isTransfer: boolean;
}

/** Screen view. The denominator for everything else on this screen. */
export function trackNanoSuccessOfferScreenViewed(properties: NanoSuccessOfferProperties) {
  analytics.track('Nano Registration > Success Offer > Screen Viewed', properties);
}

/**
 * In-view impression of the offer block itself. Distinct from the screen view: the offer sits below
 * the confirmation card, so reaching the screen is not the same as seeing the offer.
 */
export function trackNanoSuccessOfferInView(properties: NanoSuccessOfferProperties) {
  analytics.track('Nano Registration > Success Offer > Offer In View', properties);
}

/** Primary CTA — through to the shop. */
export function trackNanoSuccessOfferShopTapped(properties: NanoSuccessOfferProperties & { shopUrl: string }) {
  analytics.track('Nano Registration > Success Offer > Shop Tapped', properties);
}

/**
 * Primary CTA — through to the in-site bag instead of the shop.
 *
 * A separate name rather than a property on `Shop Tapped`, for two reasons. It is genuinely a different
 * action landing somewhere else, which is the naming rule the rest of this file follows. And the event
 * name is always queryable in the warehouse while an arbitrary property is not, so this is the one
 * dimension that reliably separates the two arms: `Screen Viewed` is the shared denominator, and the
 * tap splits into this or `Shop Tapped`.
 *
 * The legacy `> Upsell >` and `Get the Collar Button` events keep firing on both arms, because
 * Customer.io campaigns trigger on them.
 */
export function trackNanoSuccessOfferCheckoutTapped(properties: NanoSuccessOfferProperties) {
  analytics.track('Nano Registration > Success Offer > Checkout Tapped', properties);
}

/** Secondary CTA — on to the user's chips, declining the offer. */
export function trackNanoSuccessOfferSkipTapped(properties: NanoSuccessOfferProperties) {
  analytics.track('Nano Registration > Success Offer > Skip Tapped', properties);
}

/**
 * Events for the in-site bag, the screen that replaces the shop PDP.
 *
 * Same naming rule as the success-offer events above: one event per action on one screen, so the
 * funnel is a group-by on the event name with no de-duplication. `petId` lands in the warehouse
 * `value` column, which is what gives these a queryable dimension.
 *
 * `gwa` distinguishes the two offer sources, since only rescue-sourced chips resolve an offer until
 * the generic registration credit exists in web. Both keys are registered in analytics-backend's
 * allowlist; anything added here needs registering there too or it is silently dropped.
 */
export interface NanoBagProperties {
  petId: string;
  gwa: boolean;
}

/** Screen view. The denominator for the bag, replacing shop PDP views. */
export function trackNanoBagViewed(properties: NanoBagProperties) {
  analytics.track('Nano Registration > Bag > Screen Viewed', properties);
}

/** Primary CTA — through to the in-site checkout. */
export function trackNanoBagCheckoutTapped(properties: NanoBagProperties) {
  analytics.track('Nano Registration > Bag > Checkout Tapped', properties);
}

/** Secondary CTA — declining from the bag rather than from the offer screen. */
export function trackNanoBagSkipTapped(properties: NanoBagProperties) {
  analytics.track('Nano Registration > Bag > Skip Tapped', properties);
}

/**
 * Fired when the bag cannot show an offer and sends the shopper to the shop instead. This is the
 * measure of how much of the funnel the in-site flow is actually covering, so keep it firing rather
 * than treating the fallback as a silent no-op.
 *
 * The reason distinguishes "this chip has no offer" from "the offer exists but would not price",
 * which have completely different fixes. It goes in `error` because that is one of the few keys the
 * warehouse surfaces, so it stays queryable.
 */
export type NanoBagFallbackReason = 'no_offer' | 'no_price';

export function trackNanoBagFellBackToShop(properties: NanoBagProperties, reason: NanoBagFallbackReason) {
  analytics.track('Nano Registration > Bag > Fell Back To Shop', { ...properties, error: reason });
}

/** Screen view for the in-site checkout. The denominator for payment completion. */
export function trackNanoCheckoutViewed(properties: NanoBagProperties) {
  analytics.track('Nano Registration > Checkout > Screen Viewed', properties);
}

/**
 * The shopper changed the address registration prefilled, fired once per checkout. A high rate means
 * registration is capturing the wrong address, or capturing none at all.
 */
export function trackNanoCheckoutAddressEdited(properties: NanoBagProperties) {
  analytics.track('Nano Registration > Checkout > Address Edited', properties);
}

/** Submit attempt, fired before the network calls. The denominator for payment failure rate. */
export function trackNanoCheckoutPaymentSubmitted(properties: NanoBagProperties) {
  analytics.track('Nano Registration > Checkout > Payment Submitted', properties);
}

/** What was still missing when the shopper tapped Confirm on a form that could not be paid yet. */
export type NanoCheckoutMissingField = 'address' | 'payment';

const MISSING_FIELD_EVENT: Record<NanoCheckoutMissingField, string> = {
  address: 'Nano Registration > Checkout > Confirm Tapped > Address Incomplete',
  payment: 'Nano Registration > Checkout > Confirm Tapped > Payment Incomplete',
};

/**
 * Confirm was tapped before the address or payment was complete, so nothing was submitted and the
 * shopper was taken to what is missing. The gap is in the event name rather than a property, because
 * the warehouse drops custom properties.
 */
export function trackNanoCheckoutConfirmIncomplete(properties: NanoBagProperties, missing: NanoCheckoutMissingField) {
  analytics.track(MISSING_FIELD_EVENT[missing], properties);
}

/**
 * "Not now" at the foot of the checkout, which leaves for the chip list. Counted on its own so it can
 * be told apart from shoppers who reach the chip list through the menu.
 */
export function trackNanoCheckoutNotNowTapped(properties: NanoBagProperties) {
  analytics.track('Nano Registration > Checkout > Not Now Tapped', properties);
}

/**
 * Stripe found Apple Pay or Google Pay on this browser, so the wallet button can take the pay bar. The denominator for wallet taps, and the share of checkouts that can pay without typing.
 */
export function trackNanoCheckoutWalletAvailable(properties: NanoBagProperties) {
  analytics.track('Nano Registration > Checkout > Wallet Shown', properties);
}

/** Apple Pay or Google Pay in the wallet row was tapped, which opens the sheet. */
export function trackNanoCheckoutWalletTapped(properties: NanoBagProperties) {
  analytics.track('Nano Registration > Checkout > Wallet Tapped', properties);
}

/** "Pay with card" under the wallet button: the shopper chose the card form over the wallet. */
export function trackNanoCheckoutPayWithCardTapped(properties: NanoBagProperties) {
  analytics.track('Nano Registration > Checkout > Pay With Card Tapped', properties);
}

/** The purchase went through. The end of the in-site funnel. */
export function trackNanoCheckoutPurchased(properties: NanoBagProperties) {
  analytics.track('Nano Registration > Checkout > Purchased', properties);
}

/**
 * A purchase attempt failed. The reason goes in `error`, which the warehouse does not expose as a
 * column, so treat it as debug detail rather than something a dashboard can group by.
 */
export type NanoCheckoutFailureReason = 'card_declined' | 'three_d_secure' | 'address_error' | 'service_error';

export function trackNanoCheckoutFailed(properties: NanoBagProperties, reason: NanoCheckoutFailureReason) {
  analytics.track('Nano Registration > Checkout > Payment Failed', { ...properties, error: reason });
}

/**
 * How the shopper found out their email had already used the adoption offer: `precheck` is before they
 * entered a card, `purchase` is only when the purchase was rejected, which should be rare.
 */
export type NanoCheckoutOfferUsedStage = 'precheck' | 'purchase';

/**
 * The shopper was told the adoption offer no longer applies because their email had already used it,
 * and offered the shop at the regular price. The stage goes in `error`, like the fallback reasons
 * above, because that is one of the few keys the warehouse surfaces.
 */
export function trackNanoCheckoutOfferUsed(properties: NanoBagProperties, stage: NanoCheckoutOfferUsedStage) {
  analytics.track('Nano Registration > Checkout > Offer Already Used', { ...properties, error: stage });
}

/**
 * The four data-entry steps of chip registration. `upgrade` and `success` are screens rather than
 * form submissions and are instrumented above.
 */
export type NanoRegistrationStep = 'chip' | 'email' | 'pet' | 'contact';

const STEP_EVENT_SCREEN: Record<NanoRegistrationStep, string> = {
  chip: 'Chip',
  email: 'Email',
  pet: 'Pet',
  contact: 'Contact',
};

/**
 * The step is baked into the event NAME rather than passed as a property on a shared name, because
 * the warehouse exposes only a fixed set of columns from a Segment payload — the event name is
 * always queryable, an arbitrary `step` property is not.
 */
function stepEventName(step: NanoRegistrationStep, action: string) {
  return `Nano Registration > ${STEP_EVENT_SCREEN[step]} > ${action}`;
}

/** Submit attempt, fired before the network call. The denominator for per-step failure rate. */
export function trackNanoStepSubmitted(step: NanoRegistrationStep, properties: Record<string, unknown> = {}) {
  analytics.track(stepEventName(step, 'Submitted'), properties);
}

/** Fired once the step has actually advanced. */
export function trackNanoStepCompleted(step: NanoRegistrationStep, properties: Record<string, unknown> = {}) {
  analytics.track(stepEventName(step, 'Completed'), properties);
}

/**
 * Stable machine reasons for a step rejecting input or failing. Keep these values stable — they are
 * the grouping key for the drop-off analysis, so renaming one silently splits a historical series.
 */
export type NanoStepFailureReason =
  | 'invalid_format'
  | 'not_found'
  | 'already_registered'
  | 'service_error'
  | 'account_error'
  | 'validation_error';

/**
 * Fired when a step rejects the input or the request fails.
 *
 * The reason rides in `error`, which the warehouse does not expose as a column - `value` is null on
 * every row. Treat the reason as debug detail only; anything that needs to be queryable needs its
 * own event name.
 */
export function trackNanoStepFailed(
  step: NanoRegistrationStep,
  reason: NanoStepFailureReason,
  properties: Record<string, unknown> = {},
) {
  analytics.track(stepEventName(step, 'Failed'), { ...properties, error: reason });
}

/**
 * The email step put the registrant on the login form because an account already owns their email.
 *
 * Its own event name rather than a property on `Email > Failed`, because properties are not
 * queryable in the warehouse. `source` separates the lookup that avoids the error entirely from the
 * signup rejection that recovers after one; pair either with `Email > Completed` for a recovery rate.
 */
export function trackNanoEmailSwitchedToLogin(source: 'lookup' | 'signup_rejected') {
  analytics.track('Nano Registration > Email > Switched To Login', { source });
}

/** Denominator for the rescue collar question, which only adopters of a rescue's collared pet see. */
export function trackNanoRescueCollarQuestionViewed(properties: { petId: string }) {
  analytics.track('Nano Registration > Rescue Collar > Question Viewed', properties);
}

/** Recorded once the answer has been acted on: Yes opened an adoption, No returned the collar. */
export function trackNanoRescueCollarAnswered(properties: { petId: string; hasCollar: boolean }) {
  analytics.track('Nano Registration > Rescue Collar > Answered', properties);
}
